import { Dropdown } from '@ui/themed';
import { t } from 'i18next';
import { useCallback } from 'react';
import { useCustomerRoles } from '../../../customers/hooks/use-customer-roles';
import type { CustomerRole } from '../../../customers/types/role';
import { trimNodeID } from '../../hooks/use-section-tree-mutations';
import { useUpdateSection } from '../../hooks/use-sections';
import type { SectionTreeEdgeType, SectionTreeNodeSection } from '../../types/section-tree';

const CustomerEdgeType: SectionTreeEdgeType = {
  colour: 'var(--color-spark-text-secondary)',
  selectedColour: 'var(--color-spark-purple)',
  opacity: 0.5,
  width: 3,
  markerEnd: { type: 'arrowclosed', color: 'var(--color-spark-text-secondary)' },
  labelComponent: ({ ...props }) => {
    const { data: role, source: section, target: customer } = props;
    if (
      !role ||
      !section ||
      !customer ||
      !('customer_group' in section) ||
      !('email' in customer)
    ) {
      return null;
    }

    const cid = trimNodeID(customer.id);
    const sid = trimNodeID(section.id);
    if (sid === null || sid.type === 'customer' || cid === null || cid.type === 'section') {
      return null;
    }
    return (
      <RoleDropDown customerId={cid.id} section={{ ...section, id: sid.id }} currentRole={role} />
    );
  },
};

function RoleDropDown({
  customerId,
  section,
  currentRole,
}: {
  customerId: string;
  section: SectionTreeNodeSection;
  currentRole: CustomerRole;
}) {
  const { data: roleList, isLoading, isError } = useCustomerRoles();
  const updateSection = useUpdateSection(section.company_id, section.id);

  const assignRole = useCallback(
    (selected: string) =>
      updateSection.mutate({
        ...section,
        company_users: section.company_users.map(({ customer: { id }, role }) => ({
          customer_id: id,
          role_id: id === customerId ? selected : role.id,
        })),
      }),
    [updateSection, section, customerId],
  );

  return (
    <Dropdown
      options={
        roleList
          ?.filter(({ type }) => type === 'customer')
          .map((role) => ({
            value: role.id,
            label: role.name,
          })) ?? []
      }
      value={currentRole.id}
      onChange={assignRole}
      disabled={isLoading || updateSection.isPending}
      error={
        isError
          ? t('features:sections.components.role_label.role_list_error')
          : updateSection.isError
            ? t('features:sections.components.role_label.role_update_error')
            : undefined
      }
    ></Dropdown>
  );
}
export default CustomerEdgeType;
